<script setup>
import { usePlatformApi } from '@/utils/platformApi'
import { emailValidator, requiredValidator } from '@core/utils/validators'

// meta.public: true -> router.beforeEach() di plugins/1.router/guards.js
// return lebih awal untuk halaman ini, jadi SAMA SEKALI tidak melewati
// pengecekan isLoggedIn/canNavigate milik tenant. layout 'blank' -> tidak
// memakai sidebar/navigasi tenant.
definePage({ meta: { layout: 'blank', public: true } })

const router = useRouter()

const form = ref({ email: '', password: '' })
const formRef = ref()
const isPasswordVisible = ref(false)
const loading = ref(false)
const errorMessage = ref('')

// Kalau developer yang sama sudah pernah login platform admin di tab ini,
// langsung lempar ke daftar tenant. Cookie 'platformAccessToken' TIDAK
// pernah sama dengan cookie 'accessToken' tenant, jadi pengecekan ini
// tidak akan pernah salah menganggap sesi tenant sebagai sesi platform admin.
onMounted(() => {
  if (useCookie('platformAccessToken').value)
    router.push('/platform/tenants')
})

const submit = async () => {
  const { valid } = await formRef.value?.validate()
  if (!valid)
    return

  loading.value = true
  errorMessage.value = ''

  try {
    const res = await usePlatformApi('/api/v1/platform/login').post({ body: form.value })
    const body = res?.data ?? res

    // PENTING: cookie di sini (termasuk opsi maxAge apapun yang dikirim)
    // BUKAN mekanisme kontrol akses sesi 12 jam. @core/composable/useCookie.js
    // (dipakai project ini untuk cookie tenant juga) punya bug lama yang
    // memaksa maxAge jadi 30 hari terlepas dari opts yang dikirim caller —
    // TIDAK diperbaiki di sini karena akan berisiko regresi ke cookie
    // 'accessToken' tenant yang sudah stabil (di luar scope modul platform
    // admin). Jadi cookie ini murni UX: kapan browser berhenti menyimpannya
    // / kapan form login muncul lagi kalau tab ditutup-buka lama sekali.
    //
    // Kontrol akses sesungguhnya untuk sesi 12 jam ada di SERVER:
    // PlatformAuthController::login() membuat token dengan expiry eksplisit
    // (Sanctum createToken($name, $abilities, $expiresAt)), disimpan di
    // kolom `expires_at` pada personal_access_tokens. Sanctum menolak token
    // yang sudah lewat waktu itu pada SETIAP request lewat guard 'platform',
    // apapun isi/umur cookie di browser. Kalau token sudah expired di
    // server, request berikutnya ke /platform/tenants akan pulang 401 dan
    // di-redirect ke halaman ini oleh pengecekan di tenants/index.vue.
    useCookie('platformAccessToken').value = body.token
    useCookie('platformAdminData').value = body.admin

    await router.push('/platform/tenants')
  } catch (err) {
    const status = err?.status ?? err?.response?.status
    const data = err?.data ?? err?.response?._data

    errorMessage.value = status === 401
      ? 'Email atau password salah.'
      : (data?.message ?? 'Terjadi kesalahan, coba lagi.')
  } finally {
    loading.value = false
  }
}
</script>

<template>
  <div class="platform-login-wrapper d-flex align-center justify-center" style="min-height: 100vh;">
    <VCard
      max-width="420"
      class="w-100 mx-4"
      elevation="8"
    >
      <VCardText class="pa-8">
        <h4 class="text-h4 mb-1">
          Platform Admin
        </h4>
        <p class="text-body-2 text-medium-emphasis mb-6">
          Area khusus pemilik platform PintuKasir.
        </p>

        <VAlert
          v-if="errorMessage"
          type="error"
          variant="tonal"
          class="mb-4"
          density="compact"
        >
          {{ errorMessage }}
        </VAlert>

        <VForm
          ref="formRef"
          @submit.prevent="submit"
        >
          <VRow>
            <VCol cols="12">
              <VTextField
                v-model="form.email"
                label="Email"
                type="email"
                :rules="[requiredValidator, emailValidator]"
                autofocus
              />
            </VCol>

            <VCol cols="12">
              <VTextField
                v-model="form.password"
                label="Password"
                :type="isPasswordVisible ? 'text' : 'password'"
                :append-inner-icon="isPasswordVisible ? 'ri-eye-off-line' : 'ri-eye-line'"
                :rules="[requiredValidator]"
                @click:append-inner="isPasswordVisible = !isPasswordVisible"
              />
            </VCol>

            <VCol cols="12">
              <VBtn
                block
                type="submit"
                :loading="loading"
              >
                Login
              </VBtn>
            </VCol>
          </VRow>
        </VForm>
      </VCardText>
    </VCard>
  </div>
</template>
